import { format } from "date-fns";
import * as React from "react";
import { MadFragment, UserSummaryFragment } from "../../graphql/fragments";
import { MadReportFragment } from "../../graphql/fragments/MadReport";
import { DocusignEnvelopeStatus } from "../../graphql/types";
import useTranslations from "../../hooks/useTranslations";
import { Avatar, Badge, Link } from "../wombat-ui";

import { BadgeVariant } from "../wombat-ui/components/Badge";

type ReportPlateProps = {
  report: MadReportFragment;
  user: UserSummaryFragment;
  mad: MadFragment;
  showSignature: boolean;
};

function getSignatureBadgeVariant(
  status: DocusignEnvelopeStatus
): BadgeVariant {
  switch (status) {
    case "draft":
    case "sent":
    case "created":
    case "delivered":
    case "processing":
    case "signed":
      return "secondary";
    case "declined":
    case "deleted":
    case "voided":
      return "danger";
    case "completed":
      return "success";
  }
}

export const ReportPlate: React.FC<ReportPlateProps> = ({
  report,
  user,
  mad,
  showSignature = true
}) => {
  const t = useTranslations();

  return (
    <tr id={`report_plate_${report.id}`}>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <Avatar
            size={10}
            src={user.avatar}
            alt={`${user.name} profile picture`}
          />
          <div className="ml-4">
            <div className="text-sm font-medium text-dark-100">{user.name}</div>
            <div className="text-sm text-light-900">{user.email}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-light-900">
          {mad.campaign.name}
        </div>
      </td>
      {
        showSignature && (
          <td className="px-6 py-4 whitespace-nowrap">
            {report.docusignEnvelopeStatus && <Badge
              variant={getSignatureBadgeVariant(report.docusignEnvelopeStatus)}
            >
              {t(
                `reports.signature.shortStatus.${report.docusignEnvelopeStatus}` as const
              )}
            </Badge>}
          </td>  
        )
      }
      <td className="px-6 py-4 whitespace-nowrap">
        <Badge variant={report.checkedAt ? "success" : "secondary"}>
          {report.checkedAt ? (
            <span>
              {t("reports.completedOn", {
                time: (
                  <time
                    dateTime={format(
                      new Date(report.checkedAt),
                      t("dateFormat") as string
                    )}
                    className="ml-1"
                  >
                    {format(
                      new Date(report.checkedAt),
                      t("dateFormat") as string
                    )}
                  </time>
                ),
              })}
            </span>
          ) : (
            t("reports.ongoing")
          )}
        </Badge>
      </td>
      <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
        <Link
          to={`/reports/${report.id}`}
          className="text-primary-600 hover:text-primary-900"
        >
          {t("mads.reportsSection.view")}
        </Link>
      </td>
    </tr>
  );
};
